import { TodaySuggestion } from '@/features/student/StudentProgress/types';
import api from './api';

// Main Service Class
class ProgressService {
    // Learning Journey Methods
    async getLearningJourney(): Promise<LearningJourneyResponse> {
        const response = await api.get('/progress/learning-journey/');
        return response.data;
    }

    async updateLearningProgress(data: UpdateLearningProgressRequest): Promise<LearningProgress> {
        const response = await api.post('/progress/learning-progress/', data);
        return response.data;
    }

    // Learning Topics Methods
    async getLearningTopics(): Promise<LearningTopicsResponse> {
        const response = await api.get('/progress/learning-topics/');
        return response.data;
    }

    async getTopicProgress(topicId: string): Promise<TopicProgress> {
        const response = await api.get(`/progress/topics/${topicId}/`);
        return response.data;
    }

    // Today's Suggestions Methods
    async getTodaySuggestions(): Promise<TodaySuggestionsResponse> {
        const response = await api.get('/progress/today-suggestions/');
        return response.data;
    }

    async getSuggestedLessons(filters?: SuggestionFilters): Promise<SuggestedLessonsResponse> {
        const params = new URLSearchParams();

        if (filters?.skillType) params.append('skill_type', filters.skillType);
        if (filters?.difficulty) params.append('difficulty', filters.difficulty);
        if (filters?.limit) params.append('limit', filters.limit.toString());

        const response = await api.get(`/progress/suggested-lessons/${params.toString() ? '?' + params.toString() : ''}`);
        return response.data;
    }

    // Skills Progress Methods
    async getSkillsProgress(): Promise<SkillsProgressResponse> {
        const response = await api.get('/progress/skills/');
        return response.data;
    }

    async updateSkillProgress(skillType: string, progress: number): Promise<SkillProgress> {
        const response = await api.patch(`/progress/skills/${skillType}/`, { progress });
        return response.data;
    }

    // Progress Analytics Methods
    async getProgressAnalytics(period?: 'week' | 'month' | 'year'): Promise<ProgressAnalyticsResponse> {
        const params = new URLSearchParams();
        if (period) params.append('period', period);

        const response = await api.get(`/progress/analytics/${params.toString() ? '?' + params.toString() : ''}`);
        return response.data;
    }
}

// Service instance
const progressService = new ProgressService();
export default progressService;

// === TYPE DEFINITIONS ===

export interface LearningJourney {
    currentLevel: string;
    targetLevel: string;
    overallProgress: number;
    skillsProgress: SkillProgress[];
    nextLesson: {
        title: string;
        type: string;
        duration: number;
        difficulty: string;
    };
    recommendations: string[];
}

export interface SkillProgress {
    skillType: 'vocabulary' | 'grammar' | 'listening' | 'speaking' | 'reading' | 'writing';
    progress: number;
    level: string;
    trend: 'improving' | 'stable' | 'declining';
}

export interface LearningTopic {
    id: string;
    title: string;
    description: string;
    icon: string;
    color: string;
    totalLessons: number;
    completedLessons: number;
    progress: number;
    lessons: TopicLesson[];
    isLocked: boolean;
}

export interface TopicLesson {
    id: string;
    title: string;
    type: 'vocabulary' | 'grammar' | 'listening' | 'speaking' | 'reading' | 'writing';
    duration: number;
    difficulty: string;
    level: string;
    points: number;
    status: 'completed' | 'in_progress' | 'locked' | 'available';
    progress?: number;
}

export interface TopicProgress {
    topicId: string;
    totalLessons: number;
    completedLessons: number;
    inProgressLessons: number;
    progress: number;
    timeSpent: number;
    averageScore: number;
    streak: number;
    lastStudied?: string;
}

export interface ProgressAnalytics {
    totalStudyTime: number;
    lessonsCompleted: number;
    averageScore: number;
    streakDays: number;
    skillsImprovement: {
        [key: string]: {
            previous: number;
            current: number;
            change: number;
        };
    };
    weeklyProgress: {
        date: string;
        studyTime: number;
        lessonsCompleted: number;
        score: number;
    }[];
    achievements: Achievement[];
}

export interface Achievement {
    id: string;
    title: string;
    description: string;
    icon: string;
    type: 'streak' | 'score' | 'time' | 'lessons' | 'skill';
    unlockedAt: string;
    isNew?: boolean;
}

// Response Interfaces
export interface LearningJourneyResponse {
    data: LearningJourney;
    success: boolean;
    message?: string;
}

export interface LearningTopicsResponse {
    data: LearningTopic[];
    success: boolean;
    message?: string;
}

export interface TodaySuggestionsResponse {
    data: TodaySuggestion[];
    success: boolean;
    message?: string;
}

export interface SuggestedLessonsResponse {
    data: TopicLesson[];
    total: number;
    success: boolean;
    message?: string;
}

export interface SkillsProgressResponse {
    data: SkillProgress[];
    success: boolean;
    message?: string;
}

export interface ProgressAnalyticsResponse {
    data: ProgressAnalytics;
    success: boolean;
    message?: string;
}

// Request Interfaces
export interface UpdateLearningProgressRequest {
    lessonId: string;
    progress: number;
    timeSpent: number;
    score?: number;
    isCompleted: boolean;
}

export interface SuggestionFilters {
    skillType?: 'vocabulary' | 'grammar' | 'listening' | 'speaking' | 'reading' | 'writing';
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    limit?: number;
}

export interface LearningProgress {
    id: string;
    lessonId: string;
    userId: string;
    progress: number;
    timeSpent: number;
    score?: number;
    isCompleted: boolean;
    createdAt: string;
    updatedAt: string;
} 